import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { toast } from "sonner";

import {
  useDeleteRun,
  useListPlanningRuns,
  useStartPlanningRun,
  type PlanningRun,
} from "@/api/planningRun";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { formatDate, formatRelativeTime } from "@/utils/formatters";
import { PlanningRunProgressModal } from "@/views/planning/PlanningRunProgressModal";

export default function PlanningRunsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ds = useDataTableState({
    defaultSort: { id: "createdAt", desc: true },
    filterDeps: [],
  });

  const runs = useListPlanningRuns({
    ordering: ds.orderingParam,
    limit: ds.limit,
    offset: ds.offset,
  });
  const start = useStartPlanningRun();
  const remove = useDeleteRun();

  const [calculating, setCalculating] = useState<PlanningRun | null>(null);
  const [deleting, setDeleting] = useState<PlanningRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [carryOver, setCarryOver] = useState(true);
  const startRun = () =>
    start.mutate(carryOver, {
      onSuccess: setCalculating,
      onError: () =>
        toast.error(
          t("planningRuns.progress.startFailed", "Could not start the run."),
        ),
    });

  const columns = useMemo<ColumnDef<PlanningRun>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("planningRuns.columns.id"),
        size: 64,
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-mono text-xs">#{row.original.id}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: t("planningRuns.columns.created"),
        enableSorting: true,
        cell: ({ row }) => formatRelativeTime(row.original.createdAt),
        size: 160,
      },
      {
        id: "planDate",
        header: t("planningRuns.columns.planDate"),
        size: 120,
        cell: ({ row }) => formatDate(row.original.planDate),
      },
      {
        id: "computedAt",
        header: t("planningRuns.columns.computed", "Computed"),
        size: 160,
        cell: ({ row }) =>
          row.original.computedAt
            ? formatRelativeTime(row.original.computedAt)
            : "—",
      },
      {
        id: "actions",
        header: "",
        size: 180,
        cell: ({ row }) => {
          return (
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex justify-end"
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDeleting(row.original)}
              >
                {t("planningRuns.actions.delete")}
              </Button>
            </div>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("planningRuns.title")}
        meta={
          <span className="text-muted-foreground text-xs">
            {t("planningRuns.subtitle")}
          </span>
        }
        actions={
          <Button onClick={() => setStarting(true)} disabled={start.isPending}>
            {t("planningRuns.actions.startNew")}
          </Button>
        }
      />

      <DataTable<PlanningRun>
        sorting={ds.sorting}
        onSortingChange={ds.setSorting}
        pagination={ds.pagination}
        onPaginationChange={ds.setPagination}
        data={runs.data?.results ?? []}
        totalCount={runs.data?.count ?? 0}
        isLoading={runs.isLoading}
        columns={columns}
        emptyLabel={t("planningRuns.empty.runs")}
        getRowId={(r) => String(r.id)}
        onRowClick={(row) => navigate(`/planning/runs/${row.id}`)}
      />

      <PlanningRunProgressModal
        run={calculating}
        onClose={() => setCalculating(null)}
        onRetry={startRun}
        onSucceeded={() => navigate(`/planning/runs/${calculating!.id}`)}
      />

      <AlertDialog open={starting} onOpenChange={setStarting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("planningRuns.startDialog.title", "Start a new run?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "planningRuns.startDialog.description",
                "The run plans from today with the latest stock, demand and existing orders.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-3">
            <Checkbox
              id="carry-over"
              checked={carryOver}
              onCheckedChange={(checked) => setCarryOver(checked === true)}
            />
            <div className="grid gap-1">
              <Label htmlFor="carry-over">
                {t(
                  "planningRuns.startDialog.carryOver",
                  "Keep discarded orders from the latest run",
                )}
              </Label>
              <p className="text-muted-foreground text-sm">
                {t(
                  "planningRuns.startDialog.carryOverHint",
                  "Skips last until their date has passed; cancelled existing orders stay cancelled while still open. Accepted orders are not kept: once placed, they come back as existing orders.",
                )}
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.buttons.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={startRun}>
              {t("planningRuns.actions.startNew")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("planningRuns.deleteDialog.title", "Delete run #{{id}}?", {
                id: deleting?.id,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "planningRuns.deleteDialog.description",
                "Its planned orders and decisions are deleted with it.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.buttons.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate(deleting!.id)}>
              {t("planningRuns.actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
